import { AppLauncher } from "@capacitor/app-launcher";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Media } from "@capacitor-community/media";
import { SnapShare } from "capacitor-snap-share-plugin";
import { CapacitorVideoCompression } from "capacitor-video-compression-plugin";
import { TiktokShare } from "capacitor-tiktok-share-plugin";

// Destination handlers for Story Studio's bottom bar.
//
// "Composite once, share everywhere": the editor renders the frame + stickers
// to a single overlay PNG, then `buildBrandedClip` burns that overlay into the
// clip itself (native video compositing). The resulting branded MP4 is what we
// hand to Save / TikTok / Snap / the OS sheet — so the stickers ride along
// wherever the rider shares. Instagram keeps using the IG story plugin (it
// overlays the sticker layer on IG's side over the raw clip). When the
// compositing plugin isn't available (web, or before it's installed),
// `buildBrandedClip` returns null and callers fall back to the raw clip. All
// native calls are guarded so the web build no-ops gracefully.

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error || new Error("blobToBase64 failed"));
        reader.readAsDataURL(blob);
    });
}

function guessExtension(url: string, mime: string): string {
    const fromUrl = url.split("?")[0].split("#")[0].split(".").pop();
    if (fromUrl && fromUrl.length <= 5 && /^[a-z0-9]+$/i.test(fromUrl)) return fromUrl.toLowerCase();
    if (mime.includes("mp4")) return "mp4";
    if (mime.includes("quicktime")) return "mov";
    return "mp4";
}

// Download a remote URL into the app cache and return its file:// URI. Returns
// the input unchanged when it's already local or we're on web.
export async function ensureLocalFile(url: string, namePrefix: string): Promise<string> {
    if (!url) return url;
    if (url.startsWith("file://") || url.startsWith("/") || url.startsWith("capacitor://")) return url;
    if (!Capacitor.isNativePlatform()) return url;

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    const blob = await res.blob();
    const base64 = await blobToBase64(blob);
    const ext = guessExtension(url, blob.type);
    const written = await Filesystem.writeFile({
        path: `ecliptic-share/${namePrefix}-${Date.now()}.${ext}`,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
    });
    return written.uri;
}

// ─── Branded clip (frame + stickers burned into the video) ───────────────
// The native video plugin burns the overlay PNG onto the clip and returns a
// new MP4. On web (and before the native method is linked) the call throws and
// we fall back to the raw clip.

// Burn the overlay PNG into the clip → branded MP4 (file://). Returns null when
// compositing isn't available (web, or the plugin isn't linked), so callers can
// fall back to sharing the raw clip.
export async function buildBrandedClip(
    videoUrl?: string,
    overlayPngUrl?: string,
): Promise<string | null> {
    if (!videoUrl || !overlayPngUrl || !Capacitor.isNativePlatform()) return null;
    try {
        const [video, overlay] = await Promise.all([
            ensureLocalFile(videoUrl, "brand-src"),
            ensureLocalFile(overlayPngUrl, "brand-ovl"),
        ]);
        const { path } = await CapacitorVideoCompression.overlayImageOnVideo({
            videoPath: video,
            overlayPath: overlay,
        });
        return path || null;
    } catch (e) {
        // Plugin missing / web / encode failure — fall back to the raw clip.
        console.warn("buildBrandedClip unavailable; sharing raw clip instead", e);
        return null;
    }
}

export type SaveResult = "saved" | "unsupported" | "error";

// Save the clip video to the device gallery.
export async function saveClipToGallery(videoUrl?: string): Promise<SaveResult> {
    if (!videoUrl || !Capacitor.isNativePlatform()) return "unsupported";
    try {
        const local = await ensureLocalFile(videoUrl, "save");
        await Media.saveVideo({ path: local });
        return "saved";
    } catch (e) {
        console.error("saveClipToGallery failed", e);
        return "error";
    }
}

// ─── Installed-aware destinations ────────────────────────────────────────
// We show only the social apps actually on the device, and fall back to Save
// when none are installed. (iOS needs the schemes in Info.plist's
// LSApplicationQueriesSchemes; Android 11+ needs them in <queries>.)

export interface ShareTargets {
    instagram: boolean;
    tiktok: boolean;
    snapchat: boolean;
}

export type ShareTargetId = "instagram" | "tiktok" | "snapchat" | "save" | "more";

export interface OrderedTarget {
    id: ShareTargetId;
    primary: boolean;
}

// Pure: turn "what's installed" into the ordered destination bar. Installed
// social apps first (Instagram leads — it's the one with the live overlay),
// then Save, then More. With no social app installed, Save is promoted to the
// primary action so the rider still keeps their branded clip.
export function orderShareTargets(t: ShareTargets): OrderedTarget[] {
    const social: ShareTargetId[] = [];
    if (t.instagram) social.push("instagram");
    if (t.tiktok) social.push("tiktok");
    if (t.snapchat) social.push("snapchat");
    if (social.length === 0) {
        return [{ id: "save", primary: true }, { id: "more", primary: false }];
    }
    return [
        ...social.map((id) => ({ id, primary: id === "instagram" })),
        { id: "save", primary: false },
        { id: "more", primary: false },
    ];
}

// iOS resolves an installed app by URL scheme; Android's AppLauncher.canOpenUrl
// instead checks an installed PACKAGE NAME (it calls PackageManager.getPackageInfo
// on the string), so "tiktok://" never matches a package and every probe returns
// false. Probe by scheme on iOS, by package name on Android.
const URL_SCHEMES: Record<keyof ShareTargets, string> = {
    instagram: "instagram://",
    tiktok: "tiktok://",
    snapchat: "snapchat://",
};

const ANDROID_PACKAGES: Record<keyof ShareTargets, string[]> = {
    instagram: ["com.instagram.android"],
    tiktok: ["com.zhiliaoapp.musically", "com.ss.android.ugc.trill"],
    snapchat: ["com.snapchat.android"],
};

// Detect which story apps are installed. Web (and any failure) → none, so the
// row falls back to Save-primary.
export async function detectShareTargets(): Promise<ShareTargets> {
    if (!Capacitor.isNativePlatform()) return { instagram: false, tiktok: false, snapchat: false };
    const isAndroid = Capacitor.getPlatform() === "android";
    const can = async (value: string) => {
        try {
            return (await AppLauncher.canOpenUrl({ url: value })).value;
        } catch {
            return false;
        }
    };
    const probe = async (key: keyof ShareTargets): Promise<boolean> => {
        if (isAndroid) {
            for (const pkg of ANDROID_PACKAGES[key]) {
                if (await can(pkg)) return true;
            }
            return false;
        }
        return can(URL_SCHEMES[key]);
    };
    const [instagram, tiktok, snapchat] = await Promise.all([
        probe("instagram"),
        probe("tiktok"),
        probe("snapchat"),
    ]);
    return { instagram, tiktok, snapchat };
}

export type ShareResult = "shared" | "unsupported" | "error";

// Hand the clip video to the native OS share sheet (covers TikTok, Snap, etc).
//
// iOS gotcha that broke this before: passing `text` *alongside* a video file
// makes several targets (Instagram, TikTok, Snap) treat the share as a text
// payload and silently drop the video — the sheet opens but "nothing shares".
// Story apps ignore a caption anyway, so when we have a file we share the FILE
// ALONE. We also probe `Share.canShare()` first so web / unconfigured builds
// fail loud instead of throwing deep in the activity controller.
export async function shareClipSheet(
    videoUrl: string | undefined,
    title: string,
    _caption?: string,
): Promise<ShareResult> {
    if (!videoUrl || !Capacitor.isNativePlatform()) return "unsupported";
    try {
        const { value: canShare } = await Share.canShare();
        if (!canShare) return "unsupported";
        const local = await ensureLocalFile(videoUrl, "share");
        // file-only share — see note above. `dialogTitle` is Android-only.
        await Share.share({ title, files: [local], dialogTitle: title });
        return "shared";
    } catch (e) {
        // Dismissing the OS share sheet rejects with a "canceled" error — that's
        // not a failure, so don't surface an error toast for it.
        const msg = String((e as { message?: string })?.message ?? e ?? "").toLowerCase();
        if (msg.includes("cancel")) return "shared";
        console.error("shareClipSheet failed", e);
        return "error";
    }
}

// ─── Direct-to-Stories (TikTok / Snap) ────────────────────────────────────
// Each app has a "drop the clip straight into its composer" path via its own
// native SDK + developer credential (see docs/story-share-setup.md). Instagram
// uses capacitor-instagram-share-plugin (gated on a Facebook App ID); TikTok and
// Snap use capacitor-tiktok-share-plugin / capacitor-snap-share-plugin. All
// three are credential-gated: with no Client Key/ID configured the native plugin
// rejects NOT_CONFIGURED and we fall back to the OS share sheet, so the feature
// keeps working today and lights up the moment the credential is dropped in.

// The iOS Share Kit only resolves when the app returns via its redirect
// callback; if that round-trip never completes (e.g. an unregistered redirect
// URI) the promise would hang and pin the Story Studio button forever. Bound it
// with a timeout that un-sticks the UI without re-sharing (the composer was
// already launched). 90s is long enough not to interrupt a real compose.
const DIRECT_SHARE_TIMEOUT_MS = 90_000;

// Run a direct SDK share. Native plugins reject with a "CODE: detail" message.
// Only PRE-LAUNCH failures (we never handed off to the app) fall back to the OS
// share sheet — once the app has launched, re-opening the sheet would
// double-share. A user-cancel / post-launch result / timeout is terminal.
async function directShareOrSheet(
    direct: () => Promise<unknown>,
    videoUrl: string,
    title: string,
    caption?: string,
    opts?: { sheetOnFailure?: boolean },
): Promise<ShareResult> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            direct(),
            new Promise((_resolve, reject) => {
                timer = setTimeout(
                    () => reject(new Error("SHARE_TIMEOUT: no response from the app")),
                    DIRECT_SHARE_TIMEOUT_MS,
                );
            }),
        ]);
        return "shared";
    } catch (e) {
        const raw = String((e as { message?: string })?.message ?? e ?? "");
        const code = raw.split(":")[0].trim().toUpperCase();
        // Terminal once we've handed off to (or returned from) the app — never
        // re-open the OS sheet, or the rider shares twice.
        if (code === "USER_CANCELLED" || code === "SHARE_TIMEOUT") return "shared";
        if (code === "SHARE_FAILED") {
            console.warn("Direct story share failed after launch", e);
            // Snap's Creative Kit rejects (e.g. encryption_metadata_read_failure — a
            // Snap dev-portal app-attestation failure, NOT a video problem) BEFORE
            // Snapchat opens: there is no Snap return-callback in AppDelegate, so
            // nothing was handed off and the OS sheet is a safe recovery (no
            // double-share). Only callers that opt in (Snap) take this path — TikTok
            // genuinely launches and returns, so its SHARE_FAILED stays terminal.
            if (opts?.sheetOnFailure) {
                console.warn("Recoverable pre-hand-off failure; falling back to OS sheet", e);
                return shareClipSheet(videoUrl, title, caption);
            }
            return "error";
        }
        // Pre-launch problem (NOT_CONFIGURED / NOT_INSTALLED / INVALID_PATH /
        // FILEPROVIDER_ERROR / NO_ACTIVITY / PERMISSION_DENIED / STAGING_FAILED) or
        // unknown → OS sheet is a safe fallback (nothing was handed off yet).
        console.warn("Direct story share unavailable; falling back to OS sheet", e);
        return shareClipSheet(videoUrl, title, caption);
    } finally {
        if (timer) {
            clearTimeout(timer);
        }
    }
}

// TikTok — hands the branded MP4 to the TikTok post composer via the OpenSDK
// Share Kit. Falls back to the OS sheet when unconfigured / unavailable.
export async function shareToTikTokStory(
    videoUrl: string | undefined,
    title: string,
    caption?: string,
): Promise<ShareResult> {
    if (!videoUrl || !Capacitor.isNativePlatform()) return "unsupported";
    return directShareOrSheet(
        async () => {
            const local = await ensureLocalFile(videoUrl, "tiktok");
            await TiktokShare.shareVideo({ videoPath: local });
        },
        videoUrl,
        title,
        caption,
    );
}

// Snapchat — hands the branded MP4 to the Snapchat preview via Creative Kit
// (no OAuth needed). Falls back to the OS sheet when unconfigured / unavailable.
export async function shareToSnapStory(
    videoUrl: string | undefined,
    title: string,
    caption?: string,
): Promise<ShareResult> {
    if (!videoUrl || !Capacitor.isNativePlatform()) return "unsupported";
    return directShareOrSheet(
        async () => {
            const local = await ensureLocalFile(videoUrl, "snap");
            await SnapShare.shareVideo({ videoPath: local });
        },
        videoUrl,
        title,
        caption,
        // Snap rejects pre-hand-off (no Snapchat launch), so a SHARE_FAILED (e.g.
        // the dev-portal encryption_metadata_read_failure) safely recovers via the sheet.
        { sheetOnFailure: true },
    );
}
